import { useRef, useState } from 'react';
import type { HoldemActionType, HoldemOptions } from '@tv-casino/shared';
import { holdemFloatButtons } from '@tv-casino/viewmodels/components/floatAction.js';
import { holdemChipStake, holdemPresets, holdemStakeLabel } from '@tv-casino/viewmodels/components/holdemActionsModel.js';
import { ChipBet } from './ChipBet.js';
import { FloatAction, useOffscreenWatch, type FloatActionButton } from './FloatAction.js';
import styles from './HoldemActions.module.css';

interface HoldemActionsProps {
  options: HoldemOptions;
  /** The acting player's stack, chips still behind. */
  chips: number;
  /** Everything on the table, for the pot-fraction presets. */
  potTotal: number;
  /** The street's high bet, which the presets build on top of. */
  currentBet: number;
  bigBlind: number;
  busy: boolean;
  onAct: (action: HoldemActionType, amount?: number) => void | Promise<boolean>;
}

/**
 * The Hold'em decision bar: fold / check / call up top, and when the street
 * is open the same chip tray the other games bet with. Min, half pot, pot,
 * and all-in drop that size into the circle. The pile is the chips leaving
 * the stack; the button sends the street total the server already speaks
 * ("raise to"). A stack that can only shove gets All-in.
 */
export function HoldemActions({
  options,
  chips,
  potTotal,
  currentBet,
  bigBlind,
  busy,
  onAct,
}: HoldemActionsProps) {
  const isBet = options.bet;
  const canRaise = isBet || options.raise;
  const stake = holdemChipStake(options, chips);
  const presets = holdemPresets(options, stake, potTotal, currentBet, bigBlind);
  const [pile, setPile] = useState(0);
  const [fill, setFill] = useState<{ id: number; amount: number } | null>(null);
  const fillSeq = useRef(0);
  /** The decision bar is the last thing on a long hand, so it can scroll away. */
  const barWatch = useOffscreenWatch<HTMLDivElement>(true);

  const callIsAllIn = chips <= options.callAmount;
  const shoveOnly = !canRaise && chips > options.callAmount;

  /**
   * What floats when the bar itself is off-screen: the cheap way forward and
   * the way out, off the shared record both renderers read. Never a raise — a
   * pill is no place to size a bet.
   */
  const floatButtons: FloatActionButton[] = holdemFloatButtons(options, chips).map((b) => ({
    label: b.label,
    quiet: b.kind === 'quiet',
    disabled: busy,
    onClick: () => onAct(b.action),
  }));

  const place = async (added: number): Promise<boolean> => {
    const result = await onAct(isBet ? 'bet' : 'raise', stake.streetIn + added);
    return result !== false;
  };

  return (
    <div className={styles.board}>
      <div ref={barWatch.ref} className={styles.row}>
        <button type="button" className={styles.fold} disabled={busy} onClick={() => onAct('fold')}>
          Fold
        </button>
        {options.check && (
          <button type="button" className={styles.primary} disabled={busy} onClick={() => onAct('check')}>
            Check
          </button>
        )}
        {options.call && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => onAct('call')}
          >
            Call {options.callAmount.toLocaleString()}
            {callIsAllIn ? ' — all in' : ''}
          </button>
        )}
        {shoveOnly && (
          <button type="button" className={styles.primary} disabled={busy} onClick={() => onAct('all_in')}>
            All in {options.allInTo.toLocaleString()}
          </button>
        )}
      </div>

      {canRaise && stake.canSize && (
        <>
          <div className={styles.presets} role="group" aria-label="Quick raise sizes">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={pile === preset.add ? styles.presetOn : styles.preset}
                aria-pressed={pile === preset.add}
                disabled={busy}
                onClick={() => {
                  fillSeq.current += 1;
                  setFill({ id: fillSeq.current, amount: preset.add });
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <ChipBet
            key={`${stake.streetIn}:${stake.addMin}:${stake.addMax}`}
            chips={stake.addMax}
            minBet={stake.addMin}
            busy={busy}
            allowShort
            fill={fill}
            onStaged={setPile}
            placeLabel={(added) =>
              added < stake.addMin
                ? isBet
                  ? 'Bet'
                  : 'Raise'
                : holdemStakeLabel(isBet, stake.streetIn, added, options.allInTo)
            }
            onPlace={place}
          />
        </>
      )}

      <FloatAction show={barWatch.offscreen} buttons={floatButtons} />
    </div>
  );
}
